import { Box, Card, CardContent, Typography, useMediaQuery } from "@mui/material";
import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import SimilarProductCard from "../Product/SimilarProductCard";
import { useHorizontalDragScroll } from "../../utils/useHorizontalDragScroll";

const SearchCard = ({ searchResults = [] }) => {
  const navigate = useNavigate();
  const smallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const mediumScreen = useMediaQuery((theme) =>
    theme.breakpoints.between("sm", "lg"),
  );
  const isTabletOrDesktop = useMediaQuery((theme) =>
    theme.breakpoints.up("sm"),
  );

  const scrollRef = useHorizontalDragScroll();
  const cardRefs = useRef([]);
  const [currentBrandIndex, setCurrentBrandIndex] = React.useState(0);

  const filteredResults = searchResults.filter((item) => {
    return (
      item?.products &&
      Array.isArray(item.products) &&
      item.products.length > 0
    );
  });

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const scrollLeft = scrollRef.current.scrollLeft;
    const cardWidth = scrollRef.current.scrollWidth / filteredResults.length;
    const newIndex = Math.round(scrollLeft / cardWidth);
    setCurrentBrandIndex(newIndex);
  };

  const scrollLeft = () => {
    scrollRef.current?.scrollBy({ left: -300, behavior: "smooth" });
  };

  const scrollRight = () => {
    scrollRef.current?.scrollBy({ left: 300, behavior: "smooth" });
  };

  return (
    <Box sx={{ position: "relative" }}>
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{
          py: 1.5,
          px: smallScreen ? 0 : mediumScreen ? 0.5 : 1.5,
          pt: 3,
          display: "flex",
          flexDirection: "row",
          gap: smallScreen ? 2 : 3,
          overflowX: "auto",
          position: "relative",
          scrollSnapType: "x mandatory",
          scrollBehavior: "smooth",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          scrollbarWidth: "none",
        }}
      >
      {filteredResults.map((brand, index) => {
          const productCount = brand.products.length;
          const getCardWidth = () => {
            if (smallScreen) return "85%";
            if (mediumScreen) return "80%";
            // Desktop: width based on actual number of product cards visible (max 3 columns)
            if (productCount === 1) return "30%";
            if (productCount === 2) return "50%";
            return "75%"; // 3 or more products (shows 3 columns)
          };

          return (
            <Box
              key={`search-card-${brand?.name || index}`}
              sx={{
                minWidth: getCardWidth(),
                maxWidth: getCardWidth(),
                display: "flex",
                flexDirection: "column",
                flexShrink: 0,
                position: "relative",
                scrollSnapAlign: "center",
                scrollSnapStop: "always",
              }}
            >
              {/* Brand Label */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    width: "4px",
                    height: "24px",
                    borderRadius: "2px",
                    background: index % 2 === 0
                      ? "linear-gradient(180deg, #FFB79C 0%, #FFA785 100%)"
                      : "linear-gradient(180deg, #9AE0EB 0%, #7DD3E0 100%)",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: smallScreen ? "15px" : "16px",
                    fontFamily: "Poppins-Bold",
                    fontWeight: 700,
                    color: "#1a1a1a",
                    letterSpacing: "1.5px",
                    textTransform: "uppercase",
                  }}
                >
                  {brand.name}
                </Typography>
              </Box>

              {/* Products Container */}
              <Card
                sx={{
                  borderRadius: smallScreen ? "12px" : "10px",
                  display: "flex",
                  flexDirection: "column",
                  boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
                  border: "1px solid",
                  borderColor: index % 2 === 0
                    ? "rgba(255,183,156,0.15)"
                    : "rgba(154,224,235,0.15)",
                  flexShrink: 0,
                  overflow: "hidden",
                  backgroundColor: index % 2 === 0
                    ? "rgba(255,183,156,0.03)"
                    : "rgba(154,224,235,0.03)",
                  height: smallScreen ? "auto" : "550px",
                }}
              >

              {/* Products container */}
              <Box
                ref={(el) => (cardRefs.current[index] = el)}
                sx={{
                  overflowX: smallScreen ? "auto" : "visible",
                  overflowY: smallScreen ? "hidden" : "auto",
                  maxHeight: smallScreen ? "none" : "500px",
                  pb: smallScreen ? 2 : 1.5,
                  px: smallScreen ? 2 : 1.5,
                  pt: smallScreen ? 1.5 : 1.25,
                  "&::-webkit-scrollbar": {
                    height: "5px",
                    width: "5px",
                    display: "block",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#f5f5f5",
                    borderRadius: "10px",
                    mx: smallScreen ? 2 : 1.5,
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: index % 2 === 0 ? "#FFB79C" : "#9AE0EB",
                    borderRadius: "10px",
                    "&:hover": {
                      backgroundColor: index % 2 === 0 ? "#FFA785" : "#7DD3E0",
                    },
                  },
                  "&::-webkit-scrollbar-corner": {
                    backgroundColor: "transparent",
                  },
                  scrollbarWidth: "thin",
                  scrollbarColor: index % 2 === 0 ? "#FFB79C #f5f5f5" : "#9AE0EB #f5f5f5",
                }}
              >
                <SimilarProductCard
                  productData={brand.products}
                  noColor
                  oneLiner
                  index={index}
                  showAll={true}
                  verticalLayout={!smallScreen}
                />
              </Box>
            </Card>
            </Box>
          );
        })}
      </Box>

      {/* Pagination Dots */}
      {filteredResults.length > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 1,
            mt: 2,
            mb: 1,
          }}
        >
          {filteredResults.map((_, index) => (
            <Box
              key={`dot-${index}`}
              sx={{
                width: currentBrandIndex === index ? "24px" : "8px",
                height: "8px",
                borderRadius: "4px",
                backgroundColor: currentBrandIndex === index
                  ? (index % 2 === 0 ? "#FFB79C" : "#9AE0EB")
                  : "rgba(0,0,0,0.15)",
                transition: "all 0.3s ease",
                cursor: "pointer",
              }}
              onClick={() => {
                const cardWidth = scrollRef.current.scrollWidth / filteredResults.length;
                scrollRef.current?.scrollTo({
                  left: cardWidth * index,
                  behavior: "smooth",
                });
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default SearchCard;
